import * as React from 'react';
import { CheckIcon } from 'lucide-react';

import useI18n from '../../hooks/useI18n';
import { cn } from '../../lib/utils';
import { Typography } from '../Typography';
import type {
  StepperContextValue,
  StepperIndicatorProps,
  StepperItemContextValue,
  StepperItemProps,
  StepperProps,
  StepperSeparatorProps,
  StepperTitleProps,
} from './types';

const StepperContext = React.createContext<StepperContextValue | null>(null);
const StepperItemContext = React.createContext<StepperItemContextValue | null>(
  null,
);

function useRequiredContext<T>(context: React.Context<T | null>, part: string) {
  const value = React.useContext(context);
  if (!value) {
    throw new Error(`${part} must be used within its Stepper parent`);
  }
  return value;
}

/**
 * Shows the steps of a multi-step flow and marks the current one. It only
 * displays progress; moving between steps stays with the consumer.
 *
 * Renders an ordered list, so screen readers announce the step count and each
 * step's position. Name the list with `aria-label`; all props go to the list.
 * Below `md` the titles do not fit beside the circles, so they are hidden and
 * `activeTitle` shows under the steps instead.
 */
const Stepper = React.forwardRef<HTMLOListElement, StepperProps>(
  ({ activeStep, activeTitle, className, children, ...props }, ref) => (
    <StepperContext.Provider value={{ activeStep }}>
      <div
        data-slot='stepper'
        className='eye:flex eye:w-full eye:flex-col eye:gap-8'
      >
        <ol
          ref={ref}
          {...props}
          data-slot='stepper-list'
          className={cn('eye:flex eye:w-full eye:items-center', className)}
        >
          {children}
        </ol>
        {activeTitle != null && (
          // The list already gives screen readers every title.
          <Typography
            aria-hidden='true'
            data-slot='stepper-active-title'
            variant='body-s-highlight'
            noWrap
            className='eye:md:hidden'
          >
            {activeTitle}
          </Typography>
        )}
      </div>
    </StepperContext.Provider>
  ),
);
Stepper.displayName = 'Stepper';

/**
 * One step. Below `md` only its circle and line stay visible, so hide any
 * extra content there too (`eye:max-md:hidden`).
 */
const StepperItem = React.forwardRef<HTMLLIElement, StepperItemProps>(
  ({ step, completed, className, children, ...props }, ref) => {
    const { activeStep } = useRequiredContext(StepperContext, 'StepperItem');
    const { t } = useI18n();
    const status =
      step === activeStep
        ? 'active'
        : (completed ?? step < activeStep)
          ? 'completed'
          : 'inactive';

    return (
      <StepperItemContext.Provider value={{ step, status }}>
        <li
          ref={ref}
          {...props}
          data-slot='stepper-item'
          data-status={status}
          aria-current={status === 'active' ? 'step' : undefined}
          className={cn(
            'eye:flex eye:min-w-0 eye:items-center eye:gap-8 eye:not-last:flex-auto eye:max-md:gap-4',
            className,
          )}
        >
          {children}
          {/* The check mark is visual only, so the state needs words too. */}
          {status === 'completed' && (
            <span className='eye:sr-only'>{t('stepCompleted')}</span>
          )}
        </li>
      </StepperItemContext.Provider>
    );
  },
);
StepperItem.displayName = 'StepperItem';

/**
 * The step number, or a check mark once the step is completed. Hidden from
 * screen readers: the list already conveys the position.
 */
const StepperIndicator = React.forwardRef<
  HTMLSpanElement,
  StepperIndicatorProps
>(({ className, ...props }, ref) => {
  const { step, status } = useRequiredContext(
    StepperItemContext,
    'StepperIndicator',
  );

  return (
    <span
      ref={ref}
      {...props}
      // Set after the spread: a Radix trigger wrapping this part passes its
      // own data attributes through `asChild`.
      aria-hidden='true'
      data-slot='stepper-indicator'
      data-status={status}
      className={cn(
        'eye:flex eye:size-24 eye:shrink-0 eye:items-center eye:justify-center eye:rounded-full eye:border eye:border-black eye:bg-black eye:text-white',
        'eye:data-[status=inactive]:border-grey-40 eye:data-[status=inactive]:bg-grey-10 eye:data-[status=inactive]:text-grey-120',
        className,
      )}
    >
      {status === 'completed' ? (
        <CheckIcon className='eye:size-14' />
      ) : (
        step + 1
      )}
    </span>
  );
});
StepperIndicator.displayName = 'StepperIndicator';

const StepperTitle = React.forwardRef<HTMLSpanElement, StepperTitleProps>(
  ({ className, ...props }, ref) => {
    const { status } = useRequiredContext(StepperItemContext, 'StepperTitle');

    return (
      <span
        ref={ref}
        {...props}
        data-slot='stepper-title'
        data-status={status}
        className={cn(
          'eye:body-m eye:whitespace-nowrap eye:text-grey-120 eye:data-[status=active]:font-semibold eye:data-[status=active]:text-black eye:max-md:sr-only',
          className,
        )}
      />
    );
  },
);
StepperTitle.displayName = 'StepperTitle';

/** The line to the next step. Leave it out of the last item. */
const StepperSeparator = React.forwardRef<
  HTMLDivElement,
  StepperSeparatorProps
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    aria-hidden='true'
    data-slot='stepper-separator'
    className={cn(
      'eye:mr-8 eye:h-px eye:min-w-12 eye:flex-auto eye:bg-grey-40 eye:max-md:mr-4 eye:max-md:min-w-4',
      className,
    )}
  />
));
StepperSeparator.displayName = 'StepperSeparator';

export {
  Stepper,
  StepperItem,
  StepperIndicator,
  StepperTitle,
  StepperSeparator,
};
